import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import {
  onAuthStateChanged,
  User,
  signOut as fbSignOut,
} from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";

type Role = "client" | "admin" | null;

/**
 * Auto-logout windows. Clients are tighter because their portal contains
 * billing detail; admins get longer because they're often switching between
 * tools mid-task and a 15-min kick is annoying for the rare actually-AFK case.
 * Numbers in milliseconds.
 */
const IDLE_TIMEOUT_MS: Record<Exclude<Role, null>, number> = {
  client: 15 * 60 * 1000,
  admin: 30 * 60 * 1000,
};

/** Cross-tab sync key. When tab A sees activity it stamps localStorage
 * with the timestamp; tab B's storage listener treats that as activity in
 * its own window so it doesn't independently log the user out while they're
 * working in another tab. */
const ACTIVITY_KEY = "pronerdz.lastActivityAt";

/** Subset of the client doc that pages actually consume. Anything not here gets ignored. */
export interface ClientProfile {
  companyName?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  websiteUrl?: string;
  accountStatus?: string;
  billingStatus?: string;
  portalAccess?: boolean;
  activePlan?: string | null;
  nextBillingDate?: unknown;
  stripeCustomerId?: string | null;
  unreadInvoiceCount?: number;
}

interface AuthState {
  user: User | null;
  role: Role;
  clientId: string | null;
  /** Full client profile. Pages read from here instead of re-fetching clients/{id}. */
  clientProfile: ClientProfile | null;
  /** Human-friendly display name (cached for the sidebar). */
  displayName: string | null;
  /**
   * True when the signed-in client's portalAccess is false. Suspended
   * clients can still sign in but are restricted to /billing where they can
   * settle their outstanding invoice. Always false for admins.
   */
  suspended: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshClaims: () => Promise<void>;
}

const Ctx = createContext<AuthState>({
  user: null,
  role: null,
  clientId: null,
  clientProfile: null,
  displayName: null,
  suspended: false,
  loading: true,
  signOut: async () => {},
  refreshClaims: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientProfile, setClientProfile] = useState<ClientProfile | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * `forceRefresh` triggers a network round-trip to Firebase Auth. We default
   * to `false` (use the cached token) so app reloads don't pay that latency
   * — claims live in the JWT and don't change unless verifyClient or
   * bootstrapAdmin sets new ones, both of which already call refreshClaims().
   */
  async function pullClaims(u: User, forceRefresh = false) {
    const tok = await u.getIdTokenResult(forceRefresh);
    setRole((tok.claims.role as Role) ?? null);
    setClientId((tok.claims.clientId as string | null) ?? null);
  }

  // Subscribe to the client doc when we have a clientId. A live listener
  // means suspension/reactivation events from the webhook propagate to the
  // UI without a manual reload — the moment portalAccess flips back to true,
  // the suspended-state banner clears and the full nav reappears.
  useEffect(() => {
    if (role !== "client" || !clientId) {
      setClientProfile(null);
      setDisplayName(null);
      return;
    }
    const unsub = onSnapshot(
      doc(db, "clients", clientId),
      (snap) => {
        const data = snap.data() as ClientProfile | undefined;
        setClientProfile(data ?? null);
        const name = data?.contactName || data?.companyName || null;
        setDisplayName(name);
      },
      (err) => {
        // Permission errors here are non-fatal — pages will still load,
        // they just won't have the cached profile.
        console.error("AuthContext clientProfile listener:", err);
        setClientProfile(null);
        setDisplayName(null);
      }
    );
    return unsub;
  }, [role, clientId]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        // Cached token — no extra round-trip on every reload.
        await pullClaims(u, false);
      } else {
        setRole(null);
        setClientId(null);
        setClientProfile(null);
        setDisplayName(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // ─────────── Inactivity auto-logout ───────────
  // Sign the user out after IDLE_TIMEOUT_MS of no activity. Listens for
  // mouse / keyboard / touch events at the window level (capture + passive
  // so we never block default behavior). Resets are throttled so a mousemove
  // storm doesn't reset the timer 60×/sec, and cross-tab via localStorage
  // so working in tab A keeps tab B alive too.
  //
  // Effect re-runs when role changes — that's how we pick the right timeout
  // (client = 15 min, admin = 30 min). When `user` becomes null, the cleanup
  // tears everything down so signed-out / Login pages have no listeners.
  const lastResetRef = useRef(0);
  useEffect(() => {
    if (!user || !role) return;
    const timeoutMs = IDLE_TIMEOUT_MS[role];

    let timer: ReturnType<typeof setTimeout> | null = null;

    function scheduleLogout() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        // Best-effort: if signOut throws (network blip), the next activity
        // will reschedule. We don't retry — losing one timer firing is
        // strictly safer than spinning on it.
        fbSignOut(auth).catch((err) => {
          console.warn("idle auto-logout: signOut failed", err);
        });
      }, timeoutMs);
    }

    function recordActivity(stampStorage: boolean) {
      const now = Date.now();
      // Throttle: at most one reset per second. Cheap guard so a mousemove
      // storm doesn't churn setTimeout / localStorage on every frame.
      if (now - lastResetRef.current < 1000) return;
      lastResetRef.current = now;
      scheduleLogout();
      if (stampStorage) {
        try {
          localStorage.setItem(ACTIVITY_KEY, String(now));
        } catch {
          /* private mode / quota — ignore, single-tab fallback still works */
        }
      }
    }

    function onLocalActivity() {
      recordActivity(true);
    }

    function onStorage(e: StorageEvent) {
      // Activity from another tab in the same origin — count it but don't
      // re-stamp (would ping-pong storage events between tabs forever).
      if (e.key === ACTIVITY_KEY) recordActivity(false);
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") onLocalActivity();
    }

    // Activity events. We want broad coverage without blocking native
    // behavior or paying re-render cost — capture+passive on window does
    // both. `visibilitychange` resets when the tab regains focus so a user
    // returning to a stale tab doesn't get insta-logged-out before they can
    // touch anything.
    const events: Array<keyof WindowEventMap> = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "wheel",
    ];
    const opts: AddEventListenerOptions = { capture: true, passive: true };
    for (const ev of events) {
      window.addEventListener(ev, onLocalActivity, opts);
    }
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Start the clock. If the user is already idle when this effect mounts
    // (e.g., they reloaded after being away), this still gives them a fresh
    // window — strictly safer than logging out on mount.
    scheduleLogout();

    return () => {
      if (timer) clearTimeout(timer);
      for (const ev of events) {
        window.removeEventListener(ev, onLocalActivity, opts);
      }
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [user, role]);

  // A client is "suspended" when their portalAccess flag is explicitly false.
  // Defaults to false for admins (no clientProfile) and for clients whose
  // profile hasn't loaded yet — the UI only restricts AFTER we know the state.
  const suspended =
    role === "client" && clientProfile?.portalAccess === false;

  return (
    <Ctx.Provider
      value={{
        user,
        role,
        clientId,
        clientProfile,
        displayName,
        suspended,
        loading,
        signOut: async () => {
          await fbSignOut(auth);
        },
        refreshClaims: async () => {
          // Explicit refresh — used right after verifyClient / bootstrapAdmin
          // sets new custom claims so we pick them up immediately.
          if (auth.currentUser) await pullClaims(auth.currentUser, true);
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  return useContext(Ctx);
}
